import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { Caption, FsButton, FsIconButton, Heading, LoadingSwitch, Text } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { CheckIcon, ErrorIcon, EyeIcon, PencilIcon, WarningIcon } from '~/components/icons';
import { useSysconfig } from '~/components/SystemConfiguration//SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import useSecretReveal from '~/hooks/useSecretReveal';
import type {
  VitessePaymentRequestsWebhookStatus,
  VitesseWebhookCredentials,
} from '~/server_shared/openapi/data-contracts';
import colors from '~/theme/tailwind/colors';
import { reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import type { VitesseConfiguration } from './ConfigurationFormDialog';
import VitesseSetupDialog from './ConfigurationFormDialog';

const HIDDEN_SECRET = '..........';

interface DetailProps {
  label: string;
  text: string;
  isSecret?: boolean;
  className?: string;
  // For a secret the page does not load: the eye fetches it the first time it is opened.
  onReveal?: () => Promise<string | null | undefined>;
}

const Detail: React.FC<DetailProps> = ({ label, text, isSecret = false, className, onReveal }) => {
  const { isMasked, revealedText, toggleHidden } = useSecretReveal(onReveal);
  return (
    <div className={cn('min-w-40', className)}>
      <Caption variant={Caption.VARIANTS.LABEL}>{label}</Caption>
      <div className="flex items-center">
        {/* `overflow-wrap` is inherited, so `wrap-anywhere` goes on the text itself: the subscription
            id and the callback URL are single unbroken tokens that would otherwise overflow their
            flex item and collide with the next column rather than wrapping inside it. */}
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="wrap-anywhere">
          {isSecret && isMasked ? HIDDEN_SECRET : (revealedText ?? text)}
        </Text>
        {isSecret ? <FsIconButton icon={EyeIcon} onClick={toggleHidden} /> : null}
      </div>
    </div>
  );
};

interface VitesseConfigResponse {
  configuration?: VitesseConfiguration & {
    webhook_username?: string;
    webhook_password?: string;
    webhook_url?: string;
  };
}

const containerWithBorderCn = 'border border-solid border-slate-600 rounded-md p-24 mt-20 w-full';
const sectionTitleCn = 'flex items-center';
// `gap-24` rather than a margin on each Detail, so the last column has no trailing gutter and a
// wrapped row keeps the same spacing as an unwrapped one.
const sectionDetailsCn = 'mt-12 flex flex-wrap gap-24';

// Narrowed from the generated contract rather than restated, like the failure marker below.
type ExpectedEventGroup = VitessePaymentRequestsWebhookStatus['expected_event_groups'][number];

/**
 * The event types this deployment expects, under the header the server files each one under and each
 * marked present or missing, followed by anything Vitesse is sending that this deployment did not ask for.
 *
 * One row per expected type rather than a joined line, because the question an operator brings here
 * is "is `ChequeVoided` subscribed?" and a comma-separated sentence makes that a reading exercise. A
 * missing type is the failure this section exists to surface: every event of it is dropped in
 * silence, and a payment voided at Vitesse stays marked paid in CMS with its reserve still held.
 *
 * Unexpected types are listed too, and deliberately subordinate - shown, never flagged. Nothing is
 * broken by one: everything this deployment handles still arrives, and the extra is answered and
 * ignored. The reason to show it is that it is evidence the subscription was edited by hand.
 *
 * The expected set and its headers are the server's, never restated here: a second copy in the frontend
 * would drift from the handler's own registry the first time an event type is added, which is the drift
 * this list exists to catch. The server builds both from that registry, and its tests pin that the groups
 * list every expected event exactly once. Kept apart from the Stripe screen's EventSubscriptionList, which
 * it began as a copy of: Stripe's events come ungrouped.
 */
const EventSubscriptionList: React.FC<{
  enabledEvents: string[];
  expectedEvents: string[];
  expectedEventGroups: ExpectedEventGroup[];
}> = ({ enabledEvents, expectedEvents, expectedEventGroups }) => {
  const subscribed = new Set(enabledEvents);
  const expected = new Set(expectedEvents);
  const unexpectedEvents = enabledEvents.filter((eventType) => !expected.has(eventType));

  return (
    <div className="mt-12">
      <Caption variant={Caption.VARIANTS.LABEL}>{`Subscribed Events (${enabledEvents.length})`}</Caption>
      {expectedEventGroups.map((group) => (
        <div key={group.label} className="mt-12">
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
            {group.label}
          </Text>
          <ul aria-label={group.label} className="m-0 list-none p-0">
            {group.event_types.map((eventType) => {
              const isPresent = subscribed.has(eventType);
              return (
                <li key={eventType} className="mt-4 flex items-center gap-8">
                  {isPresent ? (
                    <CheckIcon iconColor={colors.green['700']} />
                  ) : (
                    <ErrorIcon iconColor={colors.red['700']} />
                  )}
                  <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="wrap-anywhere">
                    {eventType}
                  </Text>
                  {isPresent ? null : (
                    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                      not subscribed
                    </Text>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {unexpectedEvents.length > 0 ? (
        <div className="mt-12">
          <Caption variant={Caption.VARIANTS.LABEL}>Not expected by CMS</Caption>
          <ul className="m-0 list-none p-0">
            {unexpectedEvents.map((eventType) => (
              <li key={eventType} className="mt-4 flex items-center gap-8">
                <WarningIcon iconColor={colors.yellow['800']} />
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="wrap-anywhere">
                  {eventType}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

// Narrowed from the generated contract rather than restated, so a marker added on the server without
// a branch here reds `tsc` instead of falling through to the healthy rendering.
type LiveStateFailure = VitessePaymentRequestsWebhookStatus['live_state_failure'];

/**
 * The subscription's health, said out loud in every state - including the healthy one.
 *
 * Without an affirmative "Active", a working integration and one nobody has ever registered read the
 * same way: an absence of complaint. Stating it is the cheapest defence against the silent failure
 * this whole section exists to surface.
 */
const WebhookHealthChip: React.FC<{
  liveStateFailure: LiveStateFailure;
  isRegistered: boolean;
  isHealthy: boolean;
}> = ({ liveStateFailure, isRegistered, isHealthy }) => {
  // Its own state rather than folded into "Not registered". A request Vitesse never answered says
  // nothing about the subscription, and reporting one that is alive and delivering as gone is both
  // wrong and an invitation to press Register, which is how an org ends up with two of them.
  //
  // Refused is its own state again, and red rather than amber: Vitesse answered, so whatever went wrong
  // will still be wrong in an hour, where amber and the word "unreachable" both say "wait". Named for
  // the common cause; the message below is what distinguishes an unauthorised client_id from a response
  // CMS could not read.
  if (liveStateFailure === 'refused') {
    return <Chip size="small" label="Vitesse refused" className="ml-12 bg-red-100 text-slate-900" />;
  }
  if (liveStateFailure === 'unreachable') {
    return <Chip size="small" label="Vitesse unreachable" className="ml-12 bg-yellow-100 text-slate-900" />;
  }

  if (!isRegistered) {
    return <Chip size="small" label="Not registered" className="ml-12 bg-slate-200 text-slate-900" />;
  }

  // One label for every fault. The chip says whether to act; which fault it is - switched off at
  // Vitesse, a stale URL, a missing event - is spelled out by the warnings under the event list.
  if (isHealthy) {
    return <Chip size="small" label="Active" className="ml-12 bg-green-100 text-slate-900" />;
  }

  return <Chip size="small" label="Requires attention" className="ml-12 bg-red-100 text-slate-900" />;
};

/**
 * What Vitesse is actually delivering for this organization, and repairing it when it has drifted.
 *
 * The HMAC subscription that carries every Payment-Requests event, Payee Choice's among them; its
 * route keeps the older `payee_choice_webhook` name.
 *
 * Mirrors the Stripe screen's EventDestinationSection, with one deliberate difference: Sync has no
 * confirmation in front of it. Vitesse's repair is an in-place update, so the HMAC secret and any
 * deliveries still queued for retry survive it and there is nothing for a warning to warn about -
 * where Stripe's Re-register deletes a live destination and rotates its signing secret.
 */
const VitessePaymentRequestsWebhookSection: React.FC<{ organizationId: number | string }> = ({ organizationId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const url = `/sysconfig/api/v1/organizations/${organizationId}/vitesse/payee_choice_webhook`;
  const { isLoading, isError, data, reloadData } = useDataFetcher<VitessePaymentRequestsWebhookStatus>(url);

  const submit = async (path: string) => {
    setIsSubmitting(true);
    try {
      await axios.post(path);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // reloadData leaves isLoading alone by design, so the button borrows the same in-flight flag the
  // two repairs use rather than growing a second one that means the same thing.
  const retry = async () => {
    setIsSubmitting(true);
    try {
      await reloadData();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Vitesse's own enabled flag counts, and has no Stripe equivalent: a subscription can be correct in
  // every other respect and deliver nothing at all. Only a *missing* expected type counts against it;
  // one Vitesse sends that CMS does not handle is listed and never reddens the chip.
  const isHealthy = Boolean(data?.is_enabled && data?.is_url_current && data?.are_expected_events_subscribed);
  // Absent means the live read succeeded, so this is a marker rather than a flag: every other field
  // here defaults to false and means "known to be wrong", while a missing failure means "we got an
  // answer at all" and the fields below can be trusted.
  const liveStateFailure = data?.live_state_failure ?? null;

  return (
    <div className={containerWithBorderCn}>
      <div className={sectionTitleCn}>
        <Heading variant={Heading.TYPES.H4}>Vitesse Payment Requests Webhook</Heading>
        {data ? (
          <WebhookHealthChip
            liveStateFailure={liveStateFailure}
            isRegistered={data.is_registered}
            isHealthy={isHealthy}
          />
        ) : null}
      </div>
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        {liveStateFailure ? (
          <>
            {liveStateFailure === 'refused' ? (
              <>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mt-12">
                  Vitesse answered, and the answer could not be used — so what it currently holds for this organization
                  is unknown. Repeating it unchanged gets the same answer; the cause is below. Credentials not
                  authorised for webhooks are the usual one.
                </Text>
                {/* Vitesse's own words, not a paraphrase, and the only part of this panel that can name
                    the actual cause: an unauthorised client_id and a response CMS could not read both
                    land here, and the copy above cannot tell them apart. It used to reach the operator
                    verbatim in the 422 this state replaced. */}
                {data?.live_state_failure_message ? (
                  <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="mt-4 wrap-anywhere">
                    {data.live_state_failure_message}
                  </Text>
                ) : null}
              </>
            ) : (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mt-12">
                Vitesse did not answer, so what it currently holds for this organization is unknown. Try again in a few
                minutes.
              </Text>
            )}
            {/* Only when there is one: an org that has never registered reaches this state on its first
                visit during an outage, and a masked field revealing an empty string reads as a
                subscription being withheld rather than one that was never made. */}
            {data?.subscription_id ? (
              <div className={sectionDetailsCn}>
                <Detail label="Subscription ID" text={data.subscription_id} isSecret className="basis-1/3" />
              </div>
            ) : null}
            <div className={sectionDetailsCn}>
              <FsButton color="primary" variant="outlined" disabled={isSubmitting} onClick={retry}>
                Retry
              </FsButton>
            </div>
          </>
        ) : data?.is_registered ? (
          <>
            <div className={sectionDetailsCn}>
              <Detail label="Subscription ID" text={data.subscription_id || ''} isSecret className="basis-1/3" />
              <Detail label="URL" text={data.url || ''} className="basis-1/2" />
            </div>
            <EventSubscriptionList
              enabledEvents={data.enabled_events ?? []}
              expectedEvents={data.expected_events ?? []}
              expectedEventGroups={data.expected_event_groups}
            />
            {!data.is_enabled ? (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mt-12">
                The subscription is switched off at Vitesse — no events are reaching CMS at all. Sync to switch it back
                on.
              </Text>
            ) : null}
            {!data.is_url_current ? (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mt-12">
                Registered URL does not match this environment — Vitesse events are not reaching CMS. Sync to correct
                it.
              </Text>
            ) : null}
            {!data.are_expected_events_subscribed ? (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mt-12">
                Vitesse is not subscribed to every event this environment handles — the ones marked above never arrive.
                Sync to correct it.
              </Text>
            ) : null}
            <div className={sectionDetailsCn}>
              <FsButton
                color="secondary"
                variant={isHealthy ? 'outlined' : 'contained'}
                disabled={isSubmitting}
                onClick={() => submit(`${url}/sync`)}
              >
                Sync
              </FsButton>
            </div>
          </>
        ) : (
          <>
            <div className={sectionDetailsCn}>
              <FsButton color="primary" variant="contained" disabled={isSubmitting} onClick={() => submit(url)}>
                Register
              </FsButton>
            </div>
          </>
        )}
      </LoadingSwitch>
    </div>
  );
};

const InternalAdminVitesse: React.FC = () => {
  const [configurationFormOpen, setConfigurationFormOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const configUrl = `/sysconfig/api/v1/organizations/${organization.id}/vitesse/config`;
  const { isLoading, isError, data, reloadData } = useDataFetcher<VitesseConfigResponse>(configUrl);

  const vitesseConfiguration = data?.configuration;
  const isEnabled = !!vitesseConfiguration;

  const handleSubmitVitesseConfiguration = async (values: VitesseConfiguration) => {
    try {
      await axios.put(configUrl, values);
      await reloadData();
      setConfigurationFormOpen(false);
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };
  const revealWebhookPassword = async () => {
    try {
      const { data: credentials } = await axios.get<VitesseWebhookCredentials>(`${configUrl}/webhook_credentials`);
      return credentials.webhook_password;
    } catch (error) {
      await reportAxiosError(error);
      return undefined;
    }
  };
  return (
    <LoadingSwitch
      isLoading={isLoading}
      isError={isError || (isEnabled && !isLoading && !vitesseConfiguration?.client_id)}
    >
      <CardDialog noCardTitle>
        {isEnabled ? (
          <>
            <div className={containerWithBorderCn}>
              <div className={sectionTitleCn}>
                <Heading variant={Heading.TYPES.H4}>Vitesse Details</Heading>
                <FsIconButton
                  icon={PencilIcon}
                  onClick={() => {
                    setConfigurationFormOpen(true);
                  }}
                />
              </div>
              <div className={sectionDetailsCn}>
                <Detail label="Client ID" text={vitesseConfiguration?.client_id || ''} />
                <Detail label="Client Secret" text={vitesseConfiguration?.client_secret ? HIDDEN_SECRET : ''} />
                <Detail label="Hub" text={vitesseConfiguration?.hub === 'us' ? 'US' : 'Standard (UK/EU)'} />
              </div>
            </div>
            <div className={containerWithBorderCn}>
              <div className={sectionTitleCn}>
                <Heading variant={Heading.TYPES.H4}>Transaction API Callback</Heading>
              </div>
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="mt-12">
                Vitesse calls this URL when a transaction request sent through its Transaction API completes,
                authenticating with the username and password below (basic auth). CMS handles it in
                handle_vitesse_transaction_completed_webhook. It is configured separately from the Vitesse Payment
                Requests Webhook.
              </Text>
              <div className={sectionDetailsCn}>
                <Detail label="Username" text={vitesseConfiguration?.webhook_username || ''} />
                <Detail
                  label="Password"
                  text={vitesseConfiguration?.webhook_password || ''}
                  isSecret
                  onReveal={revealWebhookPassword}
                />
                <Detail label="Webhook - URL" text={vitesseConfiguration?.webhook_url || ''} />
              </div>
            </div>
            <VitessePaymentRequestsWebhookSection organizationId={organization.id} />
          </>
        ) : (
          <FsButton
            color="primary"
            variant="contained"
            className="mb-40 mt-40"
            onClick={() => {
              setConfigurationFormOpen(true);
            }}
          >
            Enable Vitesse Integration
          </FsButton>
        )}
      </CardDialog>
      {configurationFormOpen ? (
        <VitesseSetupDialog
          onSubmit={handleSubmitVitesseConfiguration}
          configuration={vitesseConfiguration}
          onClose={() => {
            setConfigurationFormOpen(false);
          }}
        />
      ) : null}
    </LoadingSwitch>
  );
};

export default InternalAdminVitesse;
